<script setup>
import {
  watch,
  ref,
  computed,
  defineAsyncComponent,
  defineProps,
  defineExpose,
} from "vue";
import PushItem from "@/components/PushItem.vue";
import { usePushesStore } from "@/store/pushes";
import { debounce } from "lodash-es";
import { storeToRefs } from "pinia";

const props = defineProps({
  filtersOpen: {
    type: Boolean,
    default: false,
  },
});

const LoadingSpinner = defineAsyncComponent(() =>
  import("@/components/LoadingSpinner.vue")
);
const pushesStore = usePushesStore();

const {
  isInitialLoad,
  isRefreshing,
} = storeToRefs(pushesStore);

const pageSize = 20;
const currentPage = ref(1);
const count = computed(() => pushesStore.pushesCount);
const containerRef = ref(null);

// Вычисляем высоту контейнера в зависимости от состояния фильтров
const containerHeight = computed(() => {
  return props.filtersOpen ? "calc(100vh - 345px)" : "calc(100vh - 200px)";
});
const displayItems = computed(() => {
  return pushesStore.filteredItems;
});

const isLoading = computed(() => {
  return pushesStore.loading;
});
const paginatedItems = computed(() => {
  const start = 0;
  const end = currentPage.value * pageSize;
  return displayItems.value.slice(start, end);
});
const hasMoreItems = computed(
  () => paginatedItems.value.length < displayItems.value.length
);

const showLoadingSpinner = computed(() => {
  return (
    isInitialLoad.value && isLoading.value && paginatedItems.value.length === 0
  );
});

const preserveScrollPosition = () => {
  if (!containerRef.value) return;

  const scrollTop = containerRef.value.scrollTop;
  const scrollHeight = containerRef.value.scrollHeight;

  return {
    scrollTop,
    scrollHeight,
    ratio: scrollTop / scrollHeight,
  };
};

// Модифицируем loadMoreItems для сохранения позиции
const loadMoreItems = debounce(() => {
  if (!hasMoreItems.value || isRefreshing.value) return;

  const scrollPosition = preserveScrollPosition();
  isRefreshing.value = true;
  currentPage.value++;

  setTimeout(() => {
    isRefreshing.value = false;

    // Восстанавливаем позицию скролла после загрузки
    if (containerRef.value && scrollPosition) {
      const newScrollHeight = containerRef.value.scrollHeight;
      const newScrollTop = scrollPosition.ratio * newScrollHeight;
      containerRef.value.scrollTop = newScrollTop;
    }
  }, 300);
}, 200);


const onScroll = () => {
  if (!containerRef.value) return;

  const { scrollTop, scrollHeight, clientHeight } = containerRef.value;

  if (scrollTop + clientHeight >= scrollHeight - 150 && hasMoreItems.value) {
    loadMoreItems();
  }
};

watch(
  () => pushesStore.pushes,
  () => {
    // Сбрасываем на первую страницу при изменении фильтров
    currentPage.value = 1;
  },
  { deep: false }
);

// const emit = defineEmits(["orderSearch"]);

// const orderSearch = (searchQuery) => {
//   emit("orderSearch", searchQuery);
// };
defineExpose({
  count,
});
</script>

<template>
  <div
    class="notifications-list-container"
    ref="containerRef"
    @scroll="onScroll"
    :style="{ height: containerHeight }"
  >
    <div v-if="showLoadingSpinner" class="spinner-container">
      <LoadingSpinner />
    </div>
    <div
      v-else-if="paginatedItems.length === 0 && !isLoading"
      class="no-pushes"
    >
      <span>Пушей не найдено</span>
    </div>
    <div v-else class="notifications-list">
      <PushItem
        v-for="push in paginatedItems"
        :key="push.id"
        :push="push"
        @order-search="orderSearch"
      />
      <div
        v-if="isRefreshing && !isInitialLoad && hasMoreItems"
        class="refresh-indicator"
      >
        <div class="refresh-dot"></div>
        <div class="refresh-dot"></div>
        <div class="refresh-dot"></div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.notifications-list-container {
  width: 100%;
  overflow-y: auto;
  box-sizing: border-box;
  position: relative;

  &::-webkit-scrollbar {
    width: 8px;
    background-color: #f1f1f1;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 4px;
    min-height: 40px;
    max-height: 80px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
}

.notifications-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-bottom: 40px;
  min-height: 100%;
}

.notifications-list::after {
  content: "";
  height: 1px;
  flex-shrink: 0;
}

.no-pushes {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;

  span {
    font-size: 14px;
    font-weight: 600;
    font-family: "Open Sans", sans-serif;
    color: #4d5460 !important;
  }
}

.spinner-container {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  width: 100%;
  max-width: 200px;
  text-align: center;

  span {
    font-size: 14px;
    font-weight: 500;
    color: #4d5460 !important;
  }
}
</style>
